import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { LoadingState } from "~/components/screens/StateScreen"
import { FullScreen } from "~/components/screens/FullScreen"
import { authClient } from "~/lib/auth-client"
import { api } from "~/trpc/react"
import { Badge } from "~/components/ui/badge"
import { Button } from "~/components/ui/button"

export function ConsentPage() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { data: session, isPending } = authClient.useSession()
	const consentCode = searchParams.get("consent_code")
	const clientId = searchParams.get("client_id")
	const scope = searchParams.get("scope")?.split(" ").filter(Boolean) ?? []
	const { data: client, isPending: clientPending } =
		api.meta.oauthClientInfo.useQuery(
			{ clientId: clientId ?? "" },
			{ enabled: clientId !== null }
		)
	const [pendingScope, setPendingScope] = useState<"authorize" | "deny" | null>(
		null
	)

	useEffect(() => {
		if (isPending || session) return
		navigate(
			`/login?redirect_uri=${encodeURIComponent(
				window.location.pathname + window.location.search
			)}`,
			{ replace: true }
		)
	}, [isPending, navigate, session])

	if (isPending || clientPending) {
		return <LoadingState scope="page" branded />
	}

	if (!consentCode || !clientId || !client) {
		return (
			<FullScreen
				title="Invalid Request"
				message="This authorization request is missing, expired, or unknown."
			/>
		)
	}

	const respond = async (accept: boolean) => {
		setPendingScope(accept ? "authorize" : "deny")
		const response = await fetch("/api/auth/oauth2/consent", {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ accept, consent_code: consentCode })
		})
		const result = (await response.json().catch(() => null)) as {
			redirectURI?: string
		} | null
		const redirectURI = response.ok ? result?.redirectURI : undefined
		if (!redirectURI) {
			toast.error(
				"Could not submit your response; the request may have expired."
			)
			setPendingScope(null)
			return
		}
		window.location.assign(redirectURI)
	}

	return (
		<FullScreen
			title={`Authorize ${client.name}`}
			contentClassName="w-lg"
			bottomContent={
				<div className="flex w-full flex-col items-center gap-4">
					<div className="flex flex-wrap justify-center gap-2">
						{scope.map((item) => (
							<Badge key={item} variant="outline">
								{item}
							</Badge>
						))}
					</div>
					<div className="flex w-full gap-3">
						<Button
							variant="outline"
							className="flex-1"
							disabled={pendingScope !== null}
							onClick={() => respond(false)}
						>
							Deny
						</Button>
						<Button
							className="flex-1"
							disabled={pendingScope !== null}
							pending={pendingScope === "authorize"}
							onClick={() => respond(true)}
						>
							Authorize
						</Button>
					</div>
				</div>
			}
		/>
	)
}
